import React, { forwardRef, ButtonHTMLAttributes, ReactNode } from 'react';
import cntl from 'cntl';

// Use standard HTML button attributes to ensure full compatibility
export interface BaseButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children?: ReactNode;
  className?: string;
  variant?: 'primary' | 'secondary' | 'ghost' | 'icon' | 'none';
  size?: 'md' | 'lg';
  style?: ButtonHTMLAttributes<HTMLButtonElement>['style'];
}

const getButtonClasses = (
  variant: string,
  size: string,
  className: string,
) => cntl`
  flex items-center justify-center
  rounded-full transition-all duration-200
  cursor-pointer disabled:cursor-not-allowed
  disabled:bg-gray-300 disabled:text-gray-500
  outline-none focus:outline-none
  w-fit
  
  ${size === 'lg' ? 'px-6 py-3 text-sm font-semibold tracking-wider' : 'px-4 py-1.5 text-sm font-semibold'}
  
  ${variant === 'primary' ? 'bg-[#007ABC] font-bold text-base text-white hover:bg-[#005f91] active:scale-95' : ''}
  ${variant === 'secondary' ? 'bg-brand-cream border-2 border-brand-truffle font-bold text-base text-brand-truffle hover:bg-brand-truffle hover:text-brand-cream active:scale-95 text-[14px]' : ''}
  ${variant === 'ghost' ? 'bg-transparent hover:bg-black/5 active:bg-black/10' : ''}
  ${variant === 'icon' ? 'bg-transparent hover:bg-transparent' : ''}
  
  ${className}
`;

export const BaseButton = forwardRef<HTMLButtonElement, BaseButtonProps>(
  (
    {
      children,
      className = '',
      variant = 'primary',
      size = 'lg',
      type = 'button',
      style = {},
      ...props
    },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        type={type}
        style={style}
        className={getButtonClasses(variant, size, className)}
        {...props} // Spreads onClick, disabled, aria-label, etc.
      >
        {children}
      </button>
    );
  },
);

BaseButton.displayName = 'BaseButton';
